/**
 * App.js - MeSafa.ao
 * Roteamento principal: Site Comercial (público), Área Cliente, Área Admin
 */
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CurrencyProvider } from "@/lib/currency";

// Public site
import PublicLayout from "@/layouts/PublicLayout";
import LandingPage from "@/pages/site/LandingPage";
import FeaturesPage from "@/pages/site/FeaturesPage";
import PricingPage from "@/pages/site/PricingPage";
import AboutPage from "@/pages/site/AboutPage";
import ContactPage from "@/pages/site/ContactPage";
import LoginPage from "@/pages/site/LoginPage";
import RegisterPage from "@/pages/site/RegisterPage";
import ForgotPasswordPage from "@/pages/site/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/site/ResetPasswordPage";
import NotFoundPage from "@/pages/site/NotFoundPage";

// Client app
import AppLayout from "@/layouts/AppLayout";
import DashboardPage from "@/pages/app/DashboardPage";
import ChannelsPage from "@/pages/app/ChannelsPage";
import PostsPage from "@/pages/app/PostsPage";
import CalendarPage from "@/pages/app/CalendarPage";
import CampaignsPage from "@/pages/app/CampaignsPage";
import ReportsPage from "@/pages/app/ReportsPage";
import SubscriptionPage from "@/pages/app/SubscriptionPage";
import SettingsPage from "@/pages/app/SettingsPage";
import SupportPage from "@/pages/app/SupportPage";
import AdvertisingPage from "@/pages/app/AdvertisingPage";

// Legal pages
import LegalPage from "@/pages/site/LegalPage";

// Admin app
import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminClientsPage from "@/pages/admin/AdminClientsPage";
import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage";
import AdminSupportPage from "@/pages/admin/AdminSupportPage";
import AdminContactPage from "@/pages/admin/AdminContactPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminChatPage from "@/pages/admin/AdminChatPage";
import AdminAdsPage from "@/pages/admin/AdminAdsPage";

const RequireAuth = ({ children, role }) => {
    const { user, ready } = useAuth();
    if (!ready) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-muted-foreground">A carregar…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/entrar" replace />;
    if (role && user.role !== role) return <Navigate to="/painel" replace />;
    return children;
};

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <CurrencyProvider>
                    <BrowserRouter>
                        <Routes>
                            {/* SITE COMERCIAL */}
                            <Route element={<PublicLayout />}>
                                <Route path="/" element={<LandingPage />} />
                                <Route path="/funcionalidades" element={<FeaturesPage />} />
                                <Route path="/precos" element={<PricingPage />} />
                                <Route path="/sobre" element={<AboutPage />} />
                                <Route path="/contacto" element={<ContactPage />} />
                                <Route path="/entrar" element={<LoginPage />} />
                                <Route path="/registo" element={<RegisterPage />} />
                                <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
                                <Route path="/repor-senha" element={<ResetPasswordPage />} />
                                <Route path="/termos" element={<LegalPage />} />
                                <Route path="/privacidade" element={<LegalPage />} />
                                <Route path="/sla" element={<LegalPage />} />
                                <Route path="/reembolso" element={<LegalPage />} />
                                <Route path="/aviso-legal" element={<LegalPage />} />
                            </Route>

                            {/* ÁREA CLIENTE */}
                            <Route
                                element={
                                    <RequireAuth role="client">
                                        <AppLayout />
                                    </RequireAuth>
                                }
                            >
                                <Route path="/painel" element={<DashboardPage />} />
                                <Route path="/canais" element={<ChannelsPage />} />
                                <Route path="/publicacoes" element={<PostsPage />} />
                                <Route path="/calendario" element={<CalendarPage />} />
                                <Route path="/campanhas" element={<CampaignsPage />} />
                                <Route path="/relatorios" element={<ReportsPage />} />
                                <Route path="/subscricao" element={<SubscriptionPage />} />
                                <Route path="/configuracoes" element={<SettingsPage />} />
                                <Route path="/suporte" element={<SupportPage />} />
                                <Route path="/publicidade" element={<AdvertisingPage />} />
                            </Route>

                            {/* ÁREA ADMIN */}
                            <Route
                                element={
                                    <RequireAuth role="admin">
                                        <AdminLayout />
                                    </RequireAuth>
                                }
                            >
                                <Route path="/admin" element={<AdminDashboardPage />} />
                                <Route path="/admin/clientes" element={<AdminClientsPage />} />
                                <Route path="/admin/pagamentos" element={<AdminPaymentsPage />} />
                                <Route path="/admin/suporte" element={<AdminSupportPage />} />
                                <Route path="/admin/mensagens" element={<AdminContactPage />} />
                                <Route path="/admin/configuracoes" element={<AdminSettingsPage />} />
                                <Route path="/admin/chat" element={<AdminChatPage />} />
                                <Route path="/admin/publicidade" element={<AdminAdsPage />} />
                            </Route>

                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    </BrowserRouter>
                    <Toaster richColors position="top-right" />
                </CurrencyProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
